import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, finalize, forkJoin, map, of, startWith, throwError } from 'rxjs';
import { ProgressLoaderService } from 'src/app/shared/services/progress-loader.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { CommonService } from 'src/app/shared/utility/common.service';
import { LongTermRentalState } from '../../long-term-rental/state/long-term-rental.state';
import { SearchedProperty } from '../../new-order/interface/new-order.interface';
import {
  ExtendedPropertyApiDetail,
  ExtendedPropertyDetail,
  PropertyRole,
} from '../../property/interface/property.interface';
import { ScenarioState } from '../../scenario/state/scenario.state';
import { ShortTermRentalState } from '../../short-term-rental/state/short-term-rental.state';
import { SyncState } from '../../sync/state/sync.state';
import { OrderApi } from '../api/order.api';
import {
  AddCompValidationStatus,
  ComparableTableDataSource,
  ExtendedComparableProperty,
  GetComparablePropertyApiResponse,
  PrimarySearchFilter,
  PropertyMap,
  ValidateComparableExistsRequest,
  ValuationType,
} from '../interface/order.interface';
import { ApproachFactoryState } from '../state/approach-factory.state';
import { CostApproachState } from '../state/cost-approach.state';
import { OrderState } from '../state/order.state';
import { SalesApproachState } from '../state/sales-approach.state';

@Injectable({
  providedIn: 'root',
})
export class ComparableService {
  constructor(
    private approachFactoryState: ApproachFactoryState,
    private commonService: CommonService,
    private costApproachState: CostApproachState,
    private longTermRentalState: LongTermRentalState,
    private orderApi: OrderApi,
    private orderState: OrderState,
    private progressLoaderService: ProgressLoaderService,
    private salesApproachState: SalesApproachState,
    private shortTermRentalState: ShortTermRentalState,
    private snackBarService: SnackBarService,
    private syncState: SyncState,
    private scenarioState: ScenarioState
  ) {
    this._listenToSelectedComparableChanges();
  }

  searchComparables(filter: PrimarySearchFilter, valuationType?: ValuationType): Observable<void> {
    const loaderId = this.progressLoaderService.show('Searching for Comparables');
    if (valuationType === ValuationType.COST) {
      filter = {
        ...filter,
        valuationType: ValuationType.COST,
      };
      delete filter.propertyTypeList;
    } else if (valuationType === ValuationType.SHORT_TERM_RENTAL) {
      filter = {
        ...filter,
        effectiveDate: new Date(),
        valuationType: ValuationType.SHORT_TERM_RENTAL,
      };
    }

    return this.orderApi
      .searchComparables(this.orderState.orderDetail?.id, this.scenarioState.activeScenario!.id, filter)
      .pipe(
        map(() => {
          return;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }),
        finalize(() => this.progressLoaderService.hide(loaderId))
      );
  }

  getComparables(valuationType?: string): Observable<boolean> {
    const requestData = {
      id: this.orderState.orderDetail?.id,
      scenarioId: this.scenarioState.activeScenario?.id,
      ...(valuationType == ValuationType.COST
        ? this.costApproachState.secondarySearchFilter
        : valuationType == ValuationType.SHORT_TERM_RENTAL
          ? this.shortTermRentalState.secondarySearchFilter
          : valuationType == ValuationType.LONG_TERM_RENTAL
            ? this.longTermRentalState.secondarySearchFilter
            : this.salesApproachState.secondarySearchFilter),
    };

    return this.orderApi.getComparables(requestData).pipe(
      map((res: GetComparablePropertyApiResponse) => {
        if (!res.comparableList) return false;

        if (valuationType == ValuationType.COST) {
          let index = 1;
          const { pageNumber, pageLimit } = this.costApproachState.secondarySearchFilter ?? {};
          this.costApproachState.comparableListLength = res.totalCount;

          /** Add index/Sl.No. to each property to display in map marker and table view */
          this.costApproachState.comparableList = res.comparableList.map((comparable: ExtendedComparableProperty) => {
            if (pageNumber && pageLimit) comparable.index = (pageNumber - 1) * pageLimit + index;
            index++;
            return comparable;
          }) as ComparableTableDataSource[];
        } else if (valuationType == ValuationType.SHORT_TERM_RENTAL) {
          let index = 1;
          const { pageNumber, pageLimit } = this.shortTermRentalState.secondarySearchFilter ?? {};
          this.shortTermRentalState.comparableListLength = res.totalCount;

          /** Add index/Sl.No. to each property to display in map marker and table view */
          this.shortTermRentalState.comparableList = res.comparableList.map(
            (comparable: ExtendedComparableProperty) => {
              if (pageNumber && pageLimit) comparable.index = (pageNumber - 1) * pageLimit + index;
              index++;
              return comparable;
            }
          ) as ComparableTableDataSource[];
        } else if (valuationType == ValuationType.LONG_TERM_RENTAL) {
          let index = 1;
          const { pageNumber, pageLimit } = this.longTermRentalState.secondarySearchFilter ?? {};
          this.longTermRentalState.comparableListLength = res.totalCount;

          /** Add index/Sl.No. to each property to display in map marker and table view */
          this.longTermRentalState.comparableList = res.comparableList.map((comparable: ExtendedComparableProperty) => {
            if (pageNumber && pageLimit) comparable.index = (pageNumber - 1) * pageLimit + index;
            index++;
            return comparable;
          }) as ComparableTableDataSource[];
        } else {
          let index = 1;
          const { pageNumber, pageLimit } = this.salesApproachState.secondarySearchFilter ?? {};

          this.salesApproachState.comparableListLength = res.totalCount;

          /** Add index/Sl.No. to each property to display in map marker and table view */
          this.salesApproachState.comparableList = res.comparableList.map((comparable: any) => {
            if (pageNumber && pageLimit) comparable.index = (pageNumber - 1) * pageLimit + index;
            index++;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return comparable;
          });
        }

        return false;
      }),
      startWith(true),
      catchError((error) => {
        this.salesApproachState.comparableList = [];
        this.salesApproachState.comparableListLength = 0;
        this.costApproachState.comparableList = [];
        this.costApproachState.comparableListLength = 0;
        this.shortTermRentalState.comparableList = [];
        this.shortTermRentalState.comparableListLength = 0;
        this.snackBarService.open('error', error.message);
        return of(false);
      })
    );
  }

  public fetchSelectedComparables(
    scenarioId: string,
    valuationType: ValuationType
  ): Observable<ExtendedPropertyDetail[]> {
    const loaderId = this.progressLoaderService.show('Getting Comparables');
    const sortBy = this.approachFactoryState.getApproachSettingOnType(valuationType)?.selectedComparableSortBy;
    return this.orderApi
      .getSelectedComparables(this.orderState.orderDetail?.id, scenarioId, valuationType, sortBy)
      .pipe(
        map((res: ExtendedPropertyApiDetail[]) => {
          if (!res) return [];

          const formattedRes: ExtendedPropertyDetail[] = this.commonService.formatPropertyData(res);

          this._clearStoreForPairs([{ scenarioId, valuationType }]);

          // Mark this pair as fetched (even if empty) to prevent unnecessary API calls
          this._markPairAsFetched(scenarioId, valuationType);

          this.approachFactoryState.setOrderedSelectedComparableIdsOnType(
            valuationType,
            formattedRes.map(({ id }) => id).filter((id): id is string => !!id)
          );

          const selectedComparableMap = this._convertComparableListToMap(formattedRes);

          const selectedComparableListWithPossibleExtras = formattedRes.map((comparable) => {
            this._addToCompStore(scenarioId, comparable);
            return this._addMissingCustomAttributesToSelectedComparable(comparable);
          });
          const selectedComparableMapWithPossibleExtras = this._convertComparableListToMap(
            selectedComparableListWithPossibleExtras
          );

          if (valuationType == ValuationType.SALES) {
            this.syncState.syncedSelectedComparableMap = structuredClone(selectedComparableMap);
            this.salesApproachState.selectedComparableMap = structuredClone(selectedComparableMapWithPossibleExtras);
          }

          if (valuationType == ValuationType.COST) {
            this.syncState.syncedCostApproachSelectedComparableMap = structuredClone(selectedComparableMap);
            this.costApproachState.selectedComparableMap = structuredClone(selectedComparableMapWithPossibleExtras);
          }

          if (valuationType == ValuationType.SHORT_TERM_RENTAL) {
            this.syncState.syncedShortTermRentSelectedComparableMap = structuredClone(selectedComparableMap);
            this.shortTermRentalState.selectedComparableMap = structuredClone(selectedComparableMapWithPossibleExtras);
          }

          if (valuationType == ValuationType.LONG_TERM_RENTAL) {
            this.syncState.syncedLongTermRentSelectedComparableMap = structuredClone(selectedComparableMap);
            this.longTermRentalState.selectedComparableMap = structuredClone(selectedComparableMapWithPossibleExtras);
          }

          return selectedComparableListWithPossibleExtras;
        }),
        catchError((error) => {
          this.snackBarService.open('error', error.message);
          return of([]);
        }),
        finalize(() => this.progressLoaderService.hide(loaderId))
      );
  }

  /**
   * Returns all the comps across all the approaches across all the scenarios.
   * Every time a new set of comps is fetched from the API, it will be updated to the property store.
   * @param filterBy?: {scenarioId?: string; valuationType?: ValuationType;}
   * @param refresh? boolean; Default=false.
   *                  If refresh=false get the available comps from store and the remaining from API.
   *                  If refresh=true ignore the values in store and fetch latest from API.
   */
  public getSelectedComparables(
    filterBy?: {
      scenarioId?: string;
      valuationType?: ValuationType;
      comparableId?: string;
    } | null,
    refresh: boolean = false
  ): Observable<ExtendedPropertyDetail[]> {
    const scenarioValuationTypePairsToFetch = this._getScenarioValuationTypePairsToFetch(filterBy, refresh);

    // If refresh=true, clear the relevant store entries before fetching
    if (refresh) {
      this._clearStoreForPairs(scenarioValuationTypePairsToFetch);
    }

    // If no pairs to fetch, return filtered results from the store directly
    if (scenarioValuationTypePairsToFetch.length === 0) {
      return of(this._getFilteredComparables(filterBy));
    }

    const fetchComparablesApiObsList = scenarioValuationTypePairsToFetch.map(({ scenarioId, valuationType }) =>
      this.fetchSelectedComparables(scenarioId, valuationType).pipe(
        map((compsList) => ({ scenarioId, valuationType, compsList }))
      )
    );

    return forkJoin(fetchComparablesApiObsList).pipe(map(() => this._getFilteredComparables(filterBy)));
  }

  downloadComparables(type: 'pdf' | 'csv') {
    const reqParams = {
      id: this.orderState.orderDetail.id,
      scenarioId: this.scenarioState.activeScenario?.id,
      type: type,
    };
    this.orderApi.downloadComparables(reqParams).subscribe();
  }

  /**
   * Clears all comparables associated with a specific scenario across all valuation types.
   * This should be called when a scenario is deleted to clean up orphaned data.
   */
  public clearComparablesForScenario(scenarioId: string): void {
    const valuationTypesToClear: ValuationType[] = [
      ...(this.orderState.orderSetting?.salesComparison?.enabled ? [ValuationType.SALES] : []),
      ...(this.orderState.orderSetting?.costApproach?.enabled ? [ValuationType.COST] : []),
      ...(this.orderState.orderSetting?.shortTermRental?.enabled ? [ValuationType.SHORT_TERM_RENTAL] : []),
      ...(this.orderState.orderSetting?.longTermRental?.enabled ? [ValuationType.LONG_TERM_RENTAL] : []),
    ];

    const pairsToClean = valuationTypesToClear.map((valuationType) => ({ scenarioId, valuationType }));

    this._clearStoreForPairs(pairsToClean);
  }

  validateComparableExists(selectedProperty: Partial<SearchedProperty>) {
    const params: ValidateComparableExistsRequest = {
      id: this.orderState.orderDetail.id,
      scenarioId: this.scenarioState.activeScenario!.id,
      listingId: selectedProperty?.ListingId ?? null,
      pmxPropertyId: selectedProperty?.PMXPropertyId ?? null,
      addressInfo: {
        streetAddress: selectedProperty?.StreetAddress ?? null,
        unitNumber: selectedProperty?.UnitNumber ?? null,
        city: selectedProperty?.City ?? null,
        state: selectedProperty?.StateOrProvince ?? null,
        zip: selectedProperty?.PostalCode ?? null,
      },
    };
    const validationStatus: AddCompValidationStatus = {
      isDuplicate: null,
      isSelected: null,
      isRemovableComparable: null,
      notification: '',
      propertyInfo: null,
    };

    return this.orderApi.validateComparableExists(params).pipe(
      map((res) => {
        if (res?.listingId && res?.propertyId) {
          const selectedStatus =
            this.approachFactoryState.getOrderValue('selectedComparableList')?.some((item: any) => {
              return item.ListingId == res.listingId;
            }) ?? false;

          /**
           * isSelected            : true -> hide select button ,  false -> show select button
           * isRemovableComparable : true -> show remove button , false -> hide remove button
           * isDuplicate           : true -> hide add button , false -> show add button
           */
          switch (res?.propertyRole) {
            case PropertyRole.SUBJECT:
            case PropertyRole.CUSTOM_SUBJECT:
              validationStatus['isDuplicate'] = true;
              validationStatus['isSelected'] = true;
              validationStatus['isRemovableComparable'] = false;
              validationStatus['notification'] = 'This is the Subject Property';
              break;

            case PropertyRole.SPECIFIC_COMPARABLE:
            case PropertyRole.CUSTOM_COMPARABLE:
              validationStatus['isDuplicate'] = true;
              validationStatus['isSelected'] = selectedStatus;
              validationStatus['isRemovableComparable'] = true;
              validationStatus['notification'] = 'This comparable already exists';

              break;

            case PropertyRole.COMPARABLE:
              validationStatus['isDuplicate'] = true;
              validationStatus['isSelected'] = selectedStatus;
              validationStatus['isRemovableComparable'] = false;
              validationStatus['notification'] = selectedStatus
                ? 'This comparable is selected'
                : 'This comparable already exists';

              break;

            default:
              validationStatus['isDuplicate'] = null;
              validationStatus['isSelected'] = null;
              validationStatus['isRemovableComparable'] = null;
              validationStatus['notification'] = '';
              break;
          }
        } else {
          validationStatus['isDuplicate'] = false;
          validationStatus['isSelected'] = null;
          validationStatus['isRemovableComparable'] = false;
          validationStatus['notification'] = '';
        }

        return { ...validationStatus, propertyInfo: res };
      }),
      catchError(() => {
        return of(null);
      })
    );
  }

  private _listenToSelectedComparableChanges(): void {
    this.salesApproachState.selectedComparableMap$.subscribe((res) => {
      const scenarioId = this.scenarioState.activeScenario?.id;
      if (scenarioId) Object.values(res).forEach((comparable) => this._addToCompStore(scenarioId, comparable));
    });

    this.costApproachState.selectedComparableMap$.subscribe((res) => {
      const scenarioId = this.scenarioState.activeScenario?.id;
      if (scenarioId) Object.values(res).forEach((comparable) => this._addToCompStore(scenarioId, comparable));
    });

    this.shortTermRentalState.selectedComparableMap$.subscribe((res) => {
      const scenarioId = this.scenarioState.activeScenario?.id;
      if (scenarioId) Object.values(res).forEach((comparable) => this._addToCompStore(scenarioId, comparable));
    });

    this.longTermRentalState.selectedComparableMap$.subscribe((res) => {
      const scenarioId = this.scenarioState.activeScenario?.id;
      if (scenarioId) Object.values(res).forEach((comparable) => this._addToCompStore(scenarioId, comparable));
    });
  }

  /** Convert a comparable list to a map with propertyId as the key */
  private _convertComparableListToMap(comparableList: ExtendedPropertyDetail[]): PropertyMap {
    const comparableMap: PropertyMap = {};
    comparableList.forEach((comparable) => {
      if (comparable.id) comparableMap[comparable.id] = structuredClone(comparable);
    });
    return comparableMap;
  }

  /**
   * Check if a selected comparable is missing a custom attribute(usually added via Custom Attribute in Grid).
   * If missing, add the custom attribute to the comparable.
   * A comparable will be missing a custom attribute if the comparable was added/selected after the custom attribute was added.
   */
  private _addMissingCustomAttributesToSelectedComparable(
    selectedComparable: ExtendedPropertyDetail
  ): ExtendedPropertyDetail {
    selectedComparable = structuredClone(selectedComparable);

    const customAttributeList = Array.from(
      new Set(
        this.approachFactoryState
          .getOrderValue('customValueAdjustmentLineItem')
          ?.map((config) => config.propertyFieldList)
          ?.flat()
      )
    );

    for (const customAttribute of customAttributeList) {
      if (!selectedComparable[customAttribute]) {
        selectedComparable[customAttribute] = {
          userRecord: null,
          inspectionRecord: null,
          rentalRecord: null,
          listingRecord: null,
          publicRecord: null,
          selectedSource: 'userRecord',
          adjustmentValue: { autoComputed: null, userEntered: null },
          asyncDataStatus: null,
          format: 'string',
          required: false,
          fieldEnumType: null,
          fieldDisplayName: null,
        };
      }
    }

    return selectedComparable;
  }

  /**
   * Determines which scenario-valuationType pairs need to be fetched based on filterBy and refresh flag.
   */
  private _getScenarioValuationTypePairsToFetch(
    filterBy?: {
      scenarioId?: string;
      valuationType?: ValuationType;
      comparableId?: string;
    } | null,
    refresh: boolean = false
  ): Array<{ scenarioId: string; valuationType: ValuationType }> {
    const allScenarioIds = filterBy?.scenarioId
      ? [filterBy.scenarioId]
      : Object.keys(this.scenarioState.scenarioMap ?? {});

    const possibleValuationTypes = filterBy?.valuationType
      ? [filterBy.valuationType]
      : [
          ...(this.orderState.orderSetting?.salesComparison?.enabled ? [ValuationType.SALES] : []),
          ...(this.orderState.orderSetting?.costApproach?.enabled ? [ValuationType.COST] : []),
          ...(this.orderState.orderSetting?.shortTermRental?.enabled ? [ValuationType.SHORT_TERM_RENTAL] : []),
          ...(this.orderState.orderSetting?.longTermRental?.enabled ? [ValuationType.LONG_TERM_RENTAL] : []),
        ];

    const possiblePairs = allScenarioIds.flatMap((scenarioId) =>
      possibleValuationTypes.map((valuationType) => ({ scenarioId, valuationType }))
    );

    if (refresh) {
      // Force fetch all applicable pairs when refreshing
      return possiblePairs;
    }

    // Only fetch pairs that are missing from the store
    const availableScenarioValuationTypeKeys = Array.from(
      this.orderState.selectedComparableStoreByScenarioValuationType.keys()
    );

    return possiblePairs.filter(
      ({ scenarioId, valuationType }) => !availableScenarioValuationTypeKeys.includes(`${scenarioId}|${valuationType}`)
    );
  }

  /**
   * Clears the store entries for the given scenario-valuationType pairs.
   */
  private _clearStoreForPairs(pairs: Array<{ scenarioId: string; valuationType: ValuationType }>): void {
    for (const { scenarioId, valuationType } of pairs) {
      const key = `${scenarioId}|${valuationType}`;
      const comparableIds = this.orderState.selectedComparableStoreByScenarioValuationType.get(key);

      if (comparableIds) {
        // Create a copy to avoid modifying the set while iterating
        const idsToRemove = Array.from(comparableIds);
        for (const comparableId of idsToRemove) {
          this._removeFromCompStore(scenarioId, comparableId);
        }
      }

      // Also remove the key itself so it's no longer marked as fetched
      this.orderState.selectedComparableStoreByScenarioValuationType.delete(key);
    }
  }

  /**
   * Marks a scenario/valuationType pair as fetched by ensuring an entry exists in the index.
   * This prevents unnecessary API calls when the API returns an empty response.
   */
  private _markPairAsFetched(scenarioId: string, valuationType: ValuationType): void {
    const key = `${scenarioId}|${valuationType}`;
    if (!this.orderState.selectedComparableStoreByScenarioValuationType.has(key)) {
      this.orderState.selectedComparableStoreByScenarioValuationType.set(key, new Set<string>());
    }
  }

  /**
   * Returns filtered comparables from the store based on filterBy criteria.
   */
  private _getFilteredComparables(
    filterBy?: {
      scenarioId?: string;
      valuationType?: ValuationType;
      comparableId?: string;
    } | null
  ): ExtendedPropertyDetail[] {
    // If filtering by a specific comparableId
    if (filterBy?.comparableId) {
      const comparable = this.orderState.propertyStore.get(filterBy.comparableId);
      if (!comparable) return [];

      // Additional filtering by scenarioId and/or valuationType if provided
      if (filterBy.valuationType && comparable.valuationType !== filterBy.valuationType) {
        return [];
      }

      // If scenarioId filter is provided, check if the comparable exists in that scenario
      if (filterBy.scenarioId) {
        const scenarioComparableIds = this.orderState.selectedComparableStoreByScenario.get(filterBy.scenarioId);
        if (!scenarioComparableIds?.has(filterBy.comparableId)) {
          return [];
        }
      }

      return [comparable];
    }

    // Use existing _getFromCompStore for scenarioId and/or valuationType filtering
    return this._getFromCompStore(filterBy?.scenarioId, filterBy?.valuationType);
  }

  private _getFromCompStore(scenarioId?: string, valuationType?: ValuationType): ExtendedPropertyDetail[] {
    let compsList: ExtendedPropertyDetail[] = [];

    if (scenarioId && valuationType) {
      compsList = this._getFromIndex(
        this.orderState.selectedComparableStoreByScenarioValuationType,
        `${scenarioId}|${valuationType}`
      );
    } else if (scenarioId) {
      compsList = this._getFromIndex(this.orderState.selectedComparableStoreByScenario, scenarioId);
    } else if (valuationType) {
      compsList = this._getFromIndex(this.orderState.selectedComparableStoreByValuationType, valuationType);
    } else {
      compsList = Array.from(this.orderState.propertyStore.values());
    }

    return compsList;
  }

  private _addToCompStore(scenarioId: string, comparable: ExtendedPropertyDetail) {
    this.orderState.propertyStore.set(comparable.id!, comparable);

    this._addToIndex(this.orderState.selectedComparableStoreByScenario, scenarioId, comparable.id!);
    this._addToIndex(this.orderState.selectedComparableStoreByValuationType, comparable.valuationType!, comparable.id!);
    this._addToIndex(
      this.orderState.selectedComparableStoreByScenarioValuationType,
      `${scenarioId}|${comparable.valuationType}`,
      comparable.id!
    );
  }

  private _removeFromCompStore(scenarioId: string, comparableId: string) {
    const comparable = this.orderState.propertyStore.get(comparableId);

    if (!comparable) return;

    this._removeFromIndex(this.orderState.selectedComparableStoreByScenario, scenarioId, comparableId);
    this._removeFromIndex(
      this.orderState.selectedComparableStoreByValuationType,
      comparable.valuationType!,
      comparableId
    );
    this._removeFromIndex(
      this.orderState.selectedComparableStoreByScenarioValuationType,
      `${scenarioId}|${comparable.valuationType}`,
      comparableId
    );

    this.orderState.propertyStore.delete(comparableId);
  }

  private _getFromIndex(index: Map<string, Set<string>>, key: string): ExtendedPropertyDetail[] {
    const set = index.get(key);
    if (!set) return [];

    return Array.from(set.values()).map((id) => this.orderState.propertyStore.get(id)!);
  }

  private _addToIndex(index: Map<string, Set<string>>, key: string, id: string): void {
    let set = index.get(key);
    if (!set) {
      set = new Set<string>();
      index.set(key, set);
    }
    set.add(id);
  }

  private _removeFromIndex(index: Map<string, Set<string>>, key: string, id: string): void {
    const set = index.get(key);
    if (!set) return;

    set.delete(id);
    if (set.size === 0) {
      index.delete(key);
    }
  }
}
